import React, { useEffect, useState } from 'react';
import { View, StyleSheet, Animated } from 'react-native';
import { Typography, Box, TextField, Button, CircularProgress, Collapse, IconButton } from '@mui/material';
import { RocketLaunch, Close, Login } from '@mui/icons-material';

interface ComingSoonScreenProps {
    storeName?: string;
    primaryColor?: string;
    onAdminLogin?: (email: string, password: string) => Promise<{ success: boolean; message: string }>;
    onPollConfig?: () => Promise<void>;
}

const POLL_INTERVAL_MS = 30_000;

export const ComingSoonScreen: React.FC<ComingSoonScreenProps> = ({
    storeName = 'This Store',
    primaryColor = '#3b82f6',
    onAdminLogin,
    onPollConfig,
}) => {
    const [fadeAnim] = useState(new Animated.Value(0));
    const [floatAnim] = useState(new Animated.Value(0));
    const [showLogin, setShowLogin] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loginLoading, setLoginLoading] = useState(false);
    const [loginError, setLoginError] = useState('');

    const darkenColor = (color: string, percent: number) => {
        const num = parseInt(color.replace('#', ''), 16);
        const amt = Math.round(2.55 * percent);
        const R = Math.max(0, (num >> 16) - amt);
        const G = Math.max(0, ((num >> 8) & 0x00ff) - amt);
        const B = Math.max(0, (num & 0x0000ff) - amt);
        return '#' + (0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1);
    };

    const darkBg = darkenColor(primaryColor, 75);
    const mediumBg = darkenColor(primaryColor, 95);

    useEffect(() => {
        Animated.timing(fadeAnim, {
            toValue: 1,
            duration: 1000,
            useNativeDriver: false,
        }).start();

        Animated.loop(
            Animated.sequence([
                Animated.timing(floatAnim, {
                    toValue: 1,
                    duration: 2500,
                    useNativeDriver: false,
                }),
                Animated.timing(floatAnim, {
                    toValue: 0,
                    duration: 2500,
                    useNativeDriver: false,
                }),
            ])
        ).start();
    }, []);

    useEffect(() => {
        if (!onPollConfig) return;
        const id = setInterval(() => { onPollConfig(); }, POLL_INTERVAL_MS);
        return () => clearInterval(id);
    }, [onPollConfig]);

    const iconFloat = floatAnim.interpolate({
        inputRange: [0, 1],
        outputRange: [0, -12],
    });

    const handleLogin = async () => {
        if (!email || !password || !onAdminLogin) return;
        setLoginError('');
        setLoginLoading(true);
        try {
            const result = await onAdminLogin(email, password);
            if (!result.success) {
                setLoginError(result.message);
            }
        } catch {
            setLoginError('Login failed. Please try again.');
        } finally {
            setLoginLoading(false);
        }
    };

    return (
        <View style={[styles.container, { backgroundColor: darkBg }]}>
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: mediumBg,
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '-10%',
                        left: '-5%',
                        right: '-5%',
                        bottom: '-10%',
                        backgroundImage: `
                            linear-gradient(${primaryColor}18 2px, transparent 2px),
                            linear-gradient(90deg, ${primaryColor}18 2px, transparent 2px)
                        `,
                        backgroundSize: '100px 100px',
                        backgroundPosition: '0 -100px',
                        opacity: 0.5,
                        transform: 'rotate(-2deg)',
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        top: '-10%',
                        left: '-5%',
                        right: '-5%',
                        bottom: '-10%',
                        backgroundImage: `
                            linear-gradient(${primaryColor}10 1px, transparent 1px),
                            linear-gradient(90deg, ${primaryColor}10 1px, transparent 1px)
                        `,
                        backgroundSize: '50px 50px',
                        backgroundPosition: '25px -75px',
                        opacity: 0.4,
                        transform: 'rotate(1deg)',
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: `radial-gradient(ellipse at 50% 35%, transparent 0%, ${darkBg}70 65%, ${darkBg} 100%)`,
                    }}
                />
            </Box>

            <Animated.View style={[styles.content, { opacity: fadeAnim }]}>
                <Box
                    sx={[
                        styles.gradientOverlay,
                        {
                            background: `radial-gradient(circle at 50% 25%, ${primaryColor}12 0%, transparent 60%)`,
                        },
                    ]}
                />

                {/* Floating Icon */}
                <Animated.View style={[styles.iconWrapper, { transform: [{ translateY: iconFloat }] }]}>
                    <Box
                        sx={{
                            backgroundColor: 'rgba(255, 255, 255, 0.08)',
                            borderRadius: '50%',
                            padding: { xs: 2, sm: 3, md: 4 },
                            border: `2px solid ${primaryColor}40`,
                            boxShadow: `
                                0 0 60px ${primaryColor}50,
                                0 0 120px ${primaryColor}30,
                                inset 0 0 30px ${primaryColor}20
                            `,
                            marginBottom: 3,
                        }}
                    >
                        <RocketLaunch
                            sx={{
                                fontSize: { xs: 25, sm: 30, md: 40 },
                                color: primaryColor,
                                filter: `drop-shadow(0 0 20px ${primaryColor}70)`,
                            }}
                        />
                    </Box>
                </Animated.View>

                {/* Store Name Badge */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        marginBottom: 1,
                        padding: '8px 24px',
                        backgroundColor: `${primaryColor}20`,
                        borderRadius: '24px',
                        border: `1px solid ${primaryColor}40`,
                    }}
                >
                    <Typography
                        variant="body1"
                        sx={{
                            color: primaryColor,
                            fontWeight: 700,
                            letterSpacing: '1px',
                        }}
                    >
                        Coming Soon
                    </Typography>
                </Box>

                <Typography
                    sx={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        fontSize: { xs: '0.55rem', sm: '0.8rem', md: '1rem' },
                        lineHeight: 1.6,
                        textAlign: 'center',
                        maxWidth: 500,
                        mb: 4,
                    }}
                >
                    We&apos;re setting things up. Check back soon!
                </Typography>

                {/* Admin Login Toggle */}
                {onAdminLogin && (
                    <Box sx={{ width: '100%', maxWidth: 360, zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        {!showLogin ? (
                            <Button
                                onClick={() => setShowLogin(true)}
                                startIcon={<Login />}
                                sx={{
                                    color: 'rgba(255, 255, 255, 0.35)',
                                    textTransform: 'none',
                                    fontSize: '0.75rem',
                                    '&:hover': {
                                        color: 'rgba(255, 255, 255, 0.6)',
                                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                    },
                                }}
                            >
                                Store Admin Login
                            </Button>
                        ) : (
                            <Collapse in={showLogin}>
                                <Box
                                    sx={{
                                        backgroundColor: 'rgba(255, 255, 255, 0.06)',
                                        borderRadius: 3,
                                        padding: 3,
                                        border: `1px solid rgba(255, 255, 255, 0.1)`,
                                        backdropFilter: 'blur(10px)',
                                    }}
                                >
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                        <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.85rem', fontWeight: 600 }}>
                                            Admin Login
                                        </Typography>
                                        <IconButton size="small" onClick={() => { setShowLogin(false); setLoginError(''); }} sx={{ color: 'rgba(255, 255, 255, 0.4)' }}>
                                            <Close fontSize="small" />
                                        </IconButton>
                                    </Box>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        placeholder="Email"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && document.getElementById('cs-password')?.focus()}
                                        sx={{
                                            mb: 1.5,
                                            '& .MuiOutlinedInput-root': {
                                                color: 'white',
                                                backgroundColor: 'rgba(255, 255, 255, 0.06)',
                                                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.15)' },
                                                '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                                                '&.Mui-focused fieldset': { borderColor: primaryColor },
                                            },
                                            '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.3)' },
                                        }}
                                    />
                                    <TextField
                                        id="cs-password"
                                        fullWidth
                                        size="small"
                                        placeholder="Password"
                                        type="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                                        sx={{
                                            mb: 2,
                                            '& .MuiOutlinedInput-root': {
                                                color: 'white',
                                                backgroundColor: 'rgba(255, 255, 255, 0.06)',
                                                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.15)' },
                                                '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                                                '&.Mui-focused fieldset': { borderColor: primaryColor },
                                            },
                                            '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.3)' },
                                        }}
                                    />
                                    {loginError && (
                                        <Typography sx={{ color: '#ef4444', fontSize: '0.75rem', mb: 1.5 }}>
                                            {loginError}
                                        </Typography>
                                    )}
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        onClick={handleLogin}
                                        disabled={loginLoading || !email || !password}
                                        sx={{
                                            backgroundColor: primaryColor,
                                            '&:hover': { backgroundColor: darkenColor(primaryColor, 15) },
                                            textTransform: 'none',
                                            fontWeight: 600,
                                        }}
                                    >
                                        {loginLoading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Sign In'}
                                    </Button>
                                </Box>
                            </Collapse>
                        )}
                    </Box>
                )}
            </Animated.View>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100%',
        height: '100%',
        zIndex: 9999,
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
        backgroundColor: '#0f172a',
    },
    content: {
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        paddingTop: 24,
        paddingBottom: 24,
        maxWidth: 700,
        width: '100%',
        zIndex: 2,
    },
    iconWrapper: {
        marginBottom: 8,
        zIndex: 2,
    },
    gradientOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1,
    },
});
